import React from 'react';
import { useSelector } from 'react-redux';
import { clientNameSelector } from '../../../../redux/auth/selectors';
import MonthlyVarReport from './functionalityComponents/monthlyVarReport/MonthlyVarReport.component';
import CombinedReports from './functionalityComponents/combinedReports/CombinedReports.component';
import RiskMetricsReport from './functionalityComponents/riskMetricsReport/RiskMetricsReport.component';
import CounterpartyReport from './functionalityComponents/counterpartyReport/CounterpartyReport.component';
import {
  activeSectionSelector,
  createSectionByIdSelector,
} from '../../../../redux/pages/selectors';
import { Grid, GridSize, Typography } from '@mui/material';
import {
  PositionDatesMap,
  usePositionDates,
} from '../../../../api/position-dates';
import { raptorPlatformIdSelector } from '../../../../redux/raptorPlatform/selectors';
import {
  createResourceCacheKey,
  GeneralOverviewPageRenderedComponentProps,
} from '../../../layout/general/SimpleGeneralPageWrapper';
import { QueryBoundary } from '../../../utilityComponents/QueryBoundary';

export interface FunctionalityHandlerProps {
  size: GridSize;
  handler: (val?: any) => void;
  cardTitle: string;
  positionDate?: string;
  Component: React.FC<any>;
  value?: Date;
  buttonTitle?: string;
  availableDates?: PositionDatesMap;
}

const Functionality: React.FC<
  GeneralOverviewPageRenderedComponentProps
> = () => {
  const clientName = useSelector(clientNameSelector);
  const activeSection = useSelector(activeSectionSelector);
  const sectionDetails = useSelector(
    createSectionByIdSelector(activeSection ?? ''),
  );
  const raptorPlatformId = useSelector(raptorPlatformIdSelector);
  const rsResources = createResourceCacheKey(sectionDetails?.rsResources);

  const { data, isLoading, isError, error } = usePositionDates(
    {
      fundTypes: sectionDetails?.fundTypes,
      rsResources,
      raptorPlatformId: raptorPlatformId || undefined,
    },
    { enabled: !!sectionDetails },
  );

  const renderedButtons: {
    [key: string]: FunctionalityHandlerProps[];
  } = {
    adepa: [
      {
        size: 12,
        handler: () => null,
        cardTitle: 'Generate Report',
        Component: CombinedReports,
      },
    ],
    trium: [
      {
        size: 12,
        handler: (num: any) => null,
        cardTitle: 'Monthly Var Report',
        Component: MonthlyVarReport,
      },
      {
        size: 12,
        handler: (num: any) => null,
        cardTitle: 'Risk Metrics Report',
        Component: RiskMetricsReport,
      },
      {
        size: 12,
        handler: (num: any) => null,
        cardTitle: 'Counterparty Report',
        Component: CounterpartyReport,
      },
    ],
  };

  const thereIsStuffToRender = clientName && clientName in renderedButtons;

  return (
    <QueryBoundary
      isLoading={isLoading}
      isError={isError}
      error={error}
      data={data}
      loadingMessages={['Collecting necessary data...']}
      errorMessage="Error collecting data."
      noDataMessage="No position dates available."
    >
      {(positionDatesMap) => (
        <Grid container spacing={2} style={{ padding: 8 }}>
          {thereIsStuffToRender ? (
            renderedButtons[clientName!].map(
              (functionalityElement: FunctionalityHandlerProps) => (
                <functionalityElement.Component
                  key={functionalityElement.cardTitle}
                  availableDates={positionDatesMap}
                  {...functionalityElement}
                />
              ),
            )
          ) : (
            <Typography variant="h1">No functionality specified</Typography>
          )}
        </Grid>
      )}
    </QueryBoundary>
  );
};

export default Functionality;
